'use client';

import { c as _c } from "react/compiler-runtime";
import { useToast } from '@common/hooks';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { AuthMutation, AuthQueryOptions } from '@/api';
import { createRouletteRequestKey } from '../_utils/createRouletteRequestKey';
import { isAppOnlyRejection } from '../_utils/isAppOnlyRejection';
import { isRejectedByServer } from '../_utils/isRejectedByServer';
type RouletteEntryResponse = Awaited<ReturnType<NonNullable<ReturnType<(typeof AuthMutation)['post:/wello/v1/auth/affiliate/partner-points/roulettes/entries']>['mutationFn']>>>;
export type RouletteSpinResult = NonNullable<RouletteEntryResponse['context']>;
interface RouletteSpinCallbacks {
  onSpun: (spin: RouletteSpinResult) => void;
  onFailed: () => void;
}
export const useRouletteSpin = (rouletteBrandId, onAppOnly) => {
  const $ = _c(20);
  const {
    toast
  } = useToast();
  const queryClient = useQueryClient();
  const [requestKey] = useState(_temp);
  const [retryRequestKey] = useState(_temp2);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = AuthMutation["post:/wello/v1/auth/affiliate/partner-points/roulettes/entries"]();
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const entry = useMutation(t0);
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = AuthMutation["post:/wello/v1/auth/affiliate/partner-points/roulettes/entries/{rouletteEntryId}/retries"]();
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const retry = useMutation(t1);
  let t2;
  if ($[2] !== onAppOnly || $[3] !== toast) {
    t2 = (onFailed, error) => {
      if (isAppOnlyRejection(error)) {
        onAppOnly();
        onFailed();
        return;
      }
      toast({
        message: isRejectedByServer(error) ? error.return_message : "\uC7A0\uC2DC \uD6C4 \uB2E4\uC2DC \uC2DC\uB3C4\uD574 \uC8FC\uC138\uC694",
        type: "fail"
      });
      onFailed();
    };
    $[2] = onAppOnly;
    $[3] = toast;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const handleFailure = t2;
  let t3;
  if ($[5] !== entry || $[6] !== handleFailure || $[7] !== queryClient || $[8] !== requestKey || $[9] !== retryRequestKey || $[10] !== rouletteBrandId) {
    t3 = t4 => {
      const {
        onSpun,
        onFailed: onFailed_0
      } = t4;
      if (entry.isPending) {
        return;
      }
      entry.mutate({
        body: {
          rouletteBrandId,
          clientRequestKey: requestKey.acquire()
        }
      }, {
        onSuccess: response => {
          if (!response.context) {
            handleFailure(onFailed_0);
            return;
          }
          requestKey.confirm();
          retryRequestKey.confirm();
          queryClient.invalidateQueries({
            queryKey: AuthQueryOptions["get:/wello/v1/auth/affiliate/partner-points/roulettes/{rouletteBrandId}"]({
              path: {
                rouletteBrandId
              }
            }).queryKey
          });
          queryClient.invalidateQueries({
            queryKey: AuthQueryOptions["get:/wello/v1/auth/affiliate/partner-points/summary"]().queryKey
          });
          onSpun(response.context);
        },
        onError: error_0 => {
          if (isRejectedByServer(error_0)) {
            requestKey.confirm();
          }
          handleFailure(onFailed_0, error_0);
        }
      });
    };
    $[5] = entry;
    $[6] = handleFailure;
    $[7] = queryClient;
    $[8] = requestKey;
    $[9] = retryRequestKey;
    $[10] = rouletteBrandId;
    $[11] = t3;
  } else {
    t3 = $[11];
  }
  const startEntry = t3;
  let t4;
  if ($[12] !== handleFailure || $[13] !== retry || $[14] !== retryRequestKey) {
    t4 = (rouletteEntryId, adpopcornSspHistoryId, t5) => {
      const {
        onSpun: onSpun_0,
        onFailed: onFailed_1
      } = t5;
      if (retry.isPending) {
        return;
      }
      retry.mutate({
        path: {
          rouletteEntryId
        },
        body: {
          clientRequestKey: retryRequestKey.acquire(),
          ...(adpopcornSspHistoryId && {
            adpopcornSspHistoryId
          })
        }
      }, {
        onSuccess: response_0 => {
          if (!response_0.context) {
            handleFailure(onFailed_1);
            return;
          }
          retryRequestKey.confirm();
          onSpun_0(response_0.context);
        },
        onError: error_1 => {
          if (isRejectedByServer(error_1)) {
            retryRequestKey.confirm();
          }
          handleFailure(onFailed_1, error_1);
        }
      });
    };
    $[12] = handleFailure;
    $[13] = retry;
    $[14] = retryRequestKey;
    $[15] = t4;
  } else {
    t4 = $[15];
  }
  const startRetry = t4;
  const t5 = entry.isPending || retry.isPending;
  let t6;
  if ($[16] !== startEntry || $[17] !== startRetry || $[18] !== t5) {
    t6 = {
      startEntry,
      startRetry,
      isSpinning: t5
    };
    $[16] = startEntry;
    $[17] = startRetry;
    $[18] = t5;
    $[19] = t6;
  } else {
    t6 = $[19];
  }
  return t6;
};
function _temp() {
  return createRouletteRequestKey();
}
function _temp2() {
  return createRouletteRequestKey();
}